import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Rectangle,
} from 'recharts';
import { getColorByIndex } from '../../utils/colors';
import { percentFormatter } from '../../utils/formatters';
import { renderLegendLabelFormatter } from './DonutChart.component';
import { ChartDataItem, TypedChartProps } from './Chart.component';
import ChartLegentContentComponent from './ChartLegendContent.component';

const CHART_PADDING = 32;
const TOOLTIP_PADDING = 20;

const StyledBarChartContainer = styled.div`
  aspect-ratio: 1;
  max-height: 100%;
  max-width: 100%;
`;

// @ts-ignore
const LABEL_COLOR = 'black';

const renderActiveShape = (props: any) => {
  const {
    x,
    y,
    width,
    height,
    fill,
    value,
    commonCount,
    name,
    showTextTooltip,
  } = props;
  const sx = x + width / 2;
  const sy = y - 10;
  const chartHeight = height + y;
  const chartSide = x > (3 * chartHeight) / 4 ? -1 : 1;
  const ex = sx + chartSide * 22;
  const ey = sy - 20;
  const textAnchor = chartSide > 0 ? 'start' : 'end';

  const valueForRender = percentFormatter(value, commonCount);
  return (
    <g>
      <Rectangle x={x} y={y} width={width} height={height} fill={fill} />
      <Rectangle x={x} y={y - 10} width={width} height={4} fill={fill} />
      <path
        d={`M${sx},${sy}L${sx},${ey}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + chartSide * 12}
        y={ey}
        textAnchor={textAnchor}
        fill={LABEL_COLOR}
      >
        {valueForRender}
      </text>
      {showTextTooltip && (
        <text
          x={TOOLTIP_PADDING + 12}
          y={props.tooltipPosition.y + height / 2 + TOOLTIP_PADDING}
          fill={LABEL_COLOR}
        >
          {name}
        </text>
      )}
    </g>
  );
};

const BarChartComponent = ({
  data,
  onClick,
  showLabels,
  legendPortalRef,
  isAnimationActive = true,
  commonCount,
  showLegend = true,
  showTextTooltip = false,
}: TypedChartProps) => {
  const [activeIndex, setActiveIndex] = useState<number | undefined>();

  const handleClick = (dataItem: ChartDataItem, index: number) => {
    if (onClick) onClick(dataItem);
  };

  const handleLabelClick = ({ value }: any) => {
    const index = data.findIndex(({ label }) => label === value);
    const item = data[index];
    handleClick(item, index);
  };

  const ChartLegendContent = useCallback(
    (props: any) => (
      <ChartLegentContentComponent {...props} portalRef={legendPortalRef} />
    ),
    [legendPortalRef],
  );

  return (
    <StyledBarChartContainer>
      <ResponsiveContainer>
        <BarChart
          data={[{}]}
          margin={{ top: CHART_PADDING + 30, bottom: CHART_PADDING }}
        >
          {data.map((entry, index) => (
            <Bar
              key={index}
              dataKey={() => data[index].value}
              name={data[index].label}
              label={
                showLabels ? { position: 'top', fill: LABEL_COLOR } : undefined
              }
              fill={getColorByIndex(index, index === activeIndex)}
              onClick={() => handleClick(entry, index)}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
              shape={
                index === activeIndex
                  ? (props: any) =>
                      renderActiveShape({
                        ...props,
                        commonCount,
                        showTextTooltip,
                      })
                  : undefined
              }
              isAnimationActive={isAnimationActive}
            />
          ))}
          {showLegend && (
            <Legend
              align="right"
              verticalAlign="middle"
              layout="vertical"
              onClick={handleLabelClick}
              content={ChartLegendContent}
              onMouseEnter={(payload, index) => setActiveIndex(index)}
              onMouseLeave={() => setActiveIndex(undefined)}
              formatter={
                showLabels
                  ? label =>
                      renderLegendLabelFormatter(label, data, commonCount)
                  : undefined
              }
            />
          )}
        </BarChart>
      </ResponsiveContainer>
    </StyledBarChartContainer>
  );
};

export default BarChartComponent;
